import { useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Eye,
  LayoutGrid,
  Map as MapIcon,
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  Shield,
  Smartphone,
  Users,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { SitesMap, type SiteMapMarker, type SiteMapPoint } from '@/pages/locations/sites-map'
import admsDevicesApi from '@/services/adms-devices'
import type { JobLocation, JobLocationPayload, LocationAttendanceProfile } from '@/types'

const LOCATION_TYPES = ['Office', 'Branch', 'Client Site', 'Job Site', 'Warehouse', 'Other', 'Site', 'Checkpost']
const TIMEZONES = ['Europe/London', 'Asia/Kolkata', 'America/Toronto', 'Asia/Dubai', 'America/New_York', 'Asia/Singapore', 'Australia/Sydney']
const COUNTRIES = ['United Kingdom', 'India', 'Canada', 'United Arab Emirates', 'Australia']
const GEOFENCE_ACTIONS = ['Block punch', 'Allow but flag for review', 'Allow with manager approval']
const DEFAULT_CENTER = { lat: 52.2405, lng: -0.9027 }

type Screen = 'list' | 'form' | 'view'
type ListView = 'cards' | 'map'
type DeviceStatus = 'active' | 'inactive' | 'remove'
type MapMode = 'pin' | 'region' | 'radius'

type WorkerOption = {
  id: string | number
  display_name?: string | null
}

type CustomerOption = {
  id: string | number
  userId?: string | number | null
  groupId?: string | number | null
  label: string
}

type FormState = {
  name: string
  code: string
  type: string
  customerId: string
  timezone: string
  addressLine1: string
  addressLine2: string
  city: string
  postcode: string
  state: string
  country: string
  latitude: string
  longitude: string
  clockQr: boolean
  clockPin: boolean
  clockKiosk: boolean
  clockGps: boolean
  managerId: string
  department: string
  outsideAction: string
  active: boolean
  mapMode: MapMode
  radiusM: string
  points: [number, number][]
  parent: string | number | null
}

const emptyForm = (center = DEFAULT_CENTER): FormState => ({
  name: '',
  code: '',
  type: 'Office',
  customerId: '',
  timezone: 'Europe/London',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  state: '',
  country: 'United Kingdom',
  latitude: center.lat.toFixed(6),
  longitude: center.lng.toFixed(6),
  clockQr: true,
  clockPin: true,
  clockKiosk: true,
  clockGps: true,
  managerId: '',
  department: '',
  outsideAction: 'Block punch',
  active: true,
  mapMode: 'pin',
  radiusM: '150',
  points: [],
  parent: null,
})

const profileOf = (location: JobLocation): LocationAttendanceProfile => (
  location.attendance_profile && typeof location.attendance_profile === 'object'
    ? location.attendance_profile
    : {}
)

const positionOf = (location: JobLocation): SiteMapPoint | null => {
  const lat = Number(location.latitude)
  const lng = Number(location.longitude)
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng }
  const [latText, lngText] = String(location.center_latlog || '').split(',')
  const parsedLat = Number(latText)
  const parsedLng = Number(lngText)
  if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) return { lat: parsedLat, lng: parsedLng }
  return null
}

const markerFromLocation = (location: JobLocation): SiteMapMarker | null => {
  const position = positionOf(location)
  if (!position) return null
  const profile = profileOf(location)
  const radius = Number(profile.geofence_radius_m)
  const hasRadius = Number.isFinite(radius) && radius > 0
  return {
    id: String(location.id),
    position,
    label: location.place || 'Location',
    radiusM: hasRadius ? radius : null,
    polygon: !hasRadius && Array.isArray(location.coordinates) && location.coordinates.length >= 3
      ? location.coordinates
      : undefined,
  }
}

const placeLabel = (location: JobLocation) => {
  const profile = profileOf(location)
  const city = [profile.city, profile.country].filter(Boolean).join(', ')
  return city || location.address || 'Address not set'
}

const composeAddress = (form: FormState) => (
  [form.addressLine1, form.addressLine2, form.city, form.state, form.postcode, form.country]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ')
)

const circlePoints = (lat: number, lng: number, radiusM: number): [number, number][] => {
  const earth = 6378137
  return Array.from({ length: 8 }, (_, index) => {
    const angle = (index / 8) * 2 * Math.PI
    const dLat = (radiusM * Math.cos(angle)) / earth
    const dLng = (radiusM * Math.sin(angle)) / (earth * Math.cos((lat * Math.PI) / 180))
    return [
      Number((lat + (dLat * 180) / Math.PI).toFixed(6)),
      Number((lng + (dLng * 180) / Math.PI).toFixed(6)),
    ] as [number, number]
  })
}

const formFromLocation = (location: JobLocation): FormState => {
  const profile = profileOf(location)
  return {
    name: location.place || '',
    code: profile.code || '',
    type: location.type || 'Site',
    customerId: location.client_user != null ? String(location.client_user) : '',
    timezone: profile.timezone || 'Europe/London',
    addressLine1: profile.address_line1 || location.address || '',
    addressLine2: profile.address_line2 || '',
    city: profile.city || '',
    postcode: profile.postcode || '',
    state: profile.state || '',
    country: profile.country || 'United Kingdom',
    latitude: location.latitude != null ? Number(location.latitude).toFixed(6) : DEFAULT_CENTER.lat.toFixed(6),
    longitude: location.longitude != null ? Number(location.longitude).toFixed(6) : DEFAULT_CENTER.lng.toFixed(6),
    clockQr: profile.clock_qr !== false,
    clockPin: profile.clock_pin !== false,
    clockKiosk: profile.clock_kiosk !== false,
    clockGps: profile.clock_gps !== false,
    managerId: location.site_manager != null ? String(location.site_manager) : '',
    department: profile.department || '',
    outsideAction: profile.outside_geofence_action || 'Block punch',
    active: location.active !== false,
    mapMode: profile.geofence_radius_m ? 'radius' : (location.coordinates?.length ? 'region' : 'pin'),
    radiusM: profile.geofence_radius_m ? String(profile.geofence_radius_m) : '150',
    points: Array.isArray(location.coordinates) ? location.coordinates.filter((point) => point.length === 2) : [],
    parent: location.parent ?? null,
  }
}

const toPayload = (form: FormState): JobLocationPayload => {
  const latitude = Number(form.latitude)
  const longitude = Number(form.longitude)
  const radius = Number(form.radiusM)
  const coordinates = form.mapMode === 'region'
    ? (form.points.length >= 3 ? form.points : null)
    : form.mapMode === 'radius' && Number.isFinite(latitude) && Number.isFinite(longitude)
      ? circlePoints(latitude, longitude, Number.isFinite(radius) && radius > 0 ? radius : 150)
      : null
  const profile: LocationAttendanceProfile = {
    code: form.code.trim(),
    timezone: form.timezone,
    address_line1: form.addressLine1.trim(),
    address_line2: form.addressLine2.trim(),
    city: form.city.trim(),
    postcode: form.postcode.trim(),
    state: form.state.trim(),
    country: form.country,
    clock_qr: form.clockQr,
    clock_pin: form.clockPin,
    clock_kiosk: form.clockKiosk,
    clock_gps: form.clockGps,
    department: form.department.trim(),
    outside_geofence_action: form.outsideAction,
    geofence_radius_m: form.mapMode === 'radius' ? (Number.isFinite(radius) ? radius : 150) : null,
  }
  return {
    place: form.name.trim(),
    type: form.type,
    client_user: form.customerId ? form.customerId : null,
    parent: form.type === 'Checkpost' ? form.parent : null,
    address: composeAddress(form),
    site_manager: form.managerId ? form.managerId : null,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    coordinates,
    center_latlog: Number.isFinite(latitude) && Number.isFinite(longitude) ? `${latitude},${longitude}` : null,
    attendance_profile: profile,
    active: form.active,
  }
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: number
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-500">{icon}</span>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-semibold leading-tight text-slate-900">{value}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

export function LocationsSitesDirectory({
  locations,
  isLoading,
  errorMessage,
  workers,
  customers = [],
  mapsReady,
  defaultCenter,
  isSaving,
  onSave,
  onDelete,
}: {
  locations: JobLocation[]
  isLoading: boolean
  errorMessage?: string | null
  workers: WorkerOption[]
  customers?: CustomerOption[]
  mapsReady: boolean
  defaultCenter?: { lat: number; lng: number } | null
  isSaving: boolean
  onSave: (payload: JobLocationPayload, id?: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}) {
  const [screen, setScreen] = useState<Screen>('list')
  const [listView, setListView] = useState<ListView>('cards')
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null)
  const [editing, setEditing] = useState<JobLocation | null>(null)
  const [viewing, setViewing] = useState<JobLocation | null>(null)
  const [form, setForm] = useState<FormState>(() => emptyForm(defaultCenter ?? DEFAULT_CENTER))
  const [formError, setFormError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<JobLocation | null>(null)
  const [deviceLocation, setDeviceLocation] = useState<JobLocation | null>(null)
  const [deviceChoice, setDeviceChoice] = useState('')
  const [deviceSerial, setDeviceSerial] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const [linkedDeviceIds, setLinkedDeviceIds] = useState<string[]>([])
  const [deviceStatuses, setDeviceStatuses] = useState<Record<string, DeviceStatus>>({})
  const [deviceError, setDeviceError] = useState('')
  const [deviceSaving, setDeviceSaving] = useState(false)
  const routerLocation = useLocation()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(routerLocation.search)
  const devicesQuery = useQuery({
    queryKey: ['adms-devices', serviceInstanceId],
    queryFn: () => admsDevicesApi.listDevices(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  const visibleLocations = useMemo(
    () => locations.filter((location) => !location.deleted),
    [locations],
  )
  const activeCount = visibleLocations.filter((location) => location.active !== false).length
  const employeeCount = visibleLocations.reduce((sum, location) => sum + Number(location.assigned_worker_count ?? 0), 0)
  const deviceCount = visibleLocations.reduce((sum, location) => sum + (location.attendance_devices?.length ?? 0), 0)
  const mapCenter = defaultCenter ?? DEFAULT_CENTER
  const mapMarkers = useMemo(
    () => visibleLocations.flatMap((location) => {
      const marker = markerFromLocation(location)
      return marker ? [marker] : []
    }),
    [visibleLocations],
  )
  const selectedMapLocation = visibleLocations.find((location) => String(location.id) === selectedMapId) ?? null
  const customerMatchFor = (location: JobLocation) => {
    if (location.client_user == null) return null
    return customers.find((customer) => (
      String(customer.id) === String(location.client_user)
      || (customer.userId != null && String(customer.userId) === String(location.client_user))
    )) ?? null
  }
  const customerLabelFor = (location: JobLocation) => {
    if (location.client_user == null) return null
    return location.client_user_info?.name || customerMatchFor(location)?.label || null
  }
  const customerHrefFor = (location: JobLocation) => {
    const match = customerMatchFor(location)
    if (!match) return null
    const params = new URLSearchParams()
    if (serviceInstanceId) params.set('service_instance_id', String(serviceInstanceId))
    if (match.groupId != null) params.set('user_group_id', String(match.groupId))
    const search = params.toString()
    return `/job/customers/${match.id}${search ? `?${search}` : ''}`
  }
  const customerChoices = useMemo(() => {
    const options = customers.map((customer) => ({ id: String(customer.id), label: customer.label }))
    if (form.customerId && !options.some((option) => option.id === form.customerId)) {
      const currentName = editing?.client_user_info?.name || `Customer ${form.customerId}`
      options.unshift({ id: form.customerId, label: currentName })
    }
    return options
  }, [customers, editing, form.customerId])

  const openCreate = () => {
    setEditing(null)
    setViewing(null)
    setForm(emptyForm(defaultCenter ?? DEFAULT_CENTER))
    setFormError('')
    setScreen('form')
  }

  const openEdit = (location: JobLocation) => {
    setEditing(location)
    setViewing(null)
    setForm(formFromLocation(location))
    setFormError('')
    setScreen('form')
  }

  const openView = (location: JobLocation) => {
    setViewing(location)
    setEditing(null)
    setScreen('view')
  }

  const allDevices = devicesQuery.data ?? []
  const deviceById = (id: string) => allDevices.find((device) => String(device.id) === id)

  const openDevices = (location: JobLocation) => {
    const ids = (location.attendance_devices ?? []).map((id) => String(id))
    setDeviceLocation(location)
    setLinkedDeviceIds(ids)
    setDeviceStatuses(Object.fromEntries(ids.map((id) => [id, deviceById(id)?.active === false ? 'inactive' : 'active'])))
    setDeviceChoice('')
    setDeviceSerial('')
    setDeviceName('')
    setDeviceError('')
  }

  const deviceOptions = allDevices.filter((device) => {
    if (!deviceLocation) return false
    const id = String(device.id)
    const free = device.location_id == null || String(device.location_id) === String(deviceLocation.id)
    return free && !linkedDeviceIds.includes(id)
  })

  const addDeviceChoice = (value: string) => {
    if (value === 'none') {
      setDeviceChoice('')
      return
    }
    if (value === 'new') {
      setDeviceChoice('new')
      return
    }
    setLinkedDeviceIds((current) => [...current, value])
    setDeviceStatuses((current) => ({ ...current, [value]: deviceById(value)?.active === false ? 'inactive' : 'active' }))
    setDeviceChoice('')
  }

  const saveDevices = async () => {
    if (!deviceLocation) return
    if (!serviceInstanceId) {
      setDeviceError('Select a service before adding a device.')
      return
    }
    setDeviceSaving(true)
    setDeviceError('')
    try {
      let ids = [...linkedDeviceIds]
      if (deviceChoice === 'new') {
        const serial = deviceSerial.trim()
        if (!serial) {
          setDeviceError('Device serial number is required.')
          setDeviceSaving(false)
          return
        }
        const created = await admsDevicesApi.registerDevice({
          service_instance_id: serviceInstanceId,
          device_sn: serial,
          name: deviceName.trim() || undefined,
          active: true,
        })
        ids.push(String(created.id))
      }
      for (const id of linkedDeviceIds) {
        const status = deviceStatuses[id] ?? 'active'
        if (status === 'remove') {
          await admsDevicesApi.updateDevice({ service_instance_id: serviceInstanceId, id, remove: true })
          ids = ids.filter((value) => value !== id)
          continue
        }
        const wasActive = deviceById(id)?.active !== false
        if (wasActive !== (status === 'active')) {
          await admsDevicesApi.updateDevice({ service_instance_id: serviceInstanceId, id, active: status === 'active' })
        }
      }
      const before = (deviceLocation.attendance_devices ?? []).map((id) => String(id)).sort().join(',')
      if ([...ids].sort().join(',') !== before) {
        await onSave({
          ...toPayload(formFromLocation(deviceLocation)),
          attendance_devices: ids,
        }, String(deviceLocation.id))
      }
      await queryClient.invalidateQueries({ queryKey: ['adms-devices'] })
      await queryClient.invalidateQueries({ queryKey: ['job-locations'] })
      setDeviceLocation(null)
    } catch (error) {
      setDeviceError(getErrorMessage(error))
    } finally {
      setDeviceSaving(false)
    }
  }

  const update = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }))

  const position = {
    lat: Number(form.latitude) || (defaultCenter?.lat ?? DEFAULT_CENTER.lat),
    lng: Number(form.longitude) || (defaultCenter?.lng ?? DEFAULT_CENTER.lng),
  }

  const handleMapClick = (lat: number, lng: number) => {
    if (form.mapMode === 'region') {
      update({
        points: form.points.length >= 8 ? form.points : [...form.points, [Number(lat.toFixed(6)), Number(lng.toFixed(6))]],
      })
      return
    }
    update({
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    })
  }

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      return
    }
    navigator.geolocation.getCurrentPosition((result) => {
      update({
        latitude: result.coords.latitude.toFixed(6),
        longitude: result.coords.longitude.toFixed(6),
      })
    })
  }

  const submit = async () => {
    if (!form.name.trim()) {
      setFormError('Location name is required.')
      return
    }
    if (form.mapMode === 'region' && form.points.length > 0 && form.points.length < 3) {
      setFormError('Draw at least 3 boundary points, or clear the region.')
      return
    }
    setFormError('')
    try {
      await onSave(toPayload(form), editing ? String(editing.id) : undefined)
      setScreen('list')
    } catch (error) {
      setFormError(getErrorMessage(error))
    }
  }

  if (screen === 'form') {
    return (
      <div className="space-y-5 pb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{editing ? 'Edit Location' : 'Create Location'}</h1>
          <p className="mt-1 text-sm text-slate-500">Add a work location, pin it on the map and define an optional geofence for geo-tagged attendance.</p>
        </div>
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
          <section className="space-y-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <Section icon={<MapPin className="h-4 w-4" />} title="Location details" description="Basic information used across attendance and reports.">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Location name">
                  <Input value={form.name} placeholder="e.g. Northampton Head Office" onChange={(event) => update({ name: event.target.value })} />
                </Field>
                <Field label="Location code">
                  <Input value={form.code} placeholder="NHO-01" onChange={(event) => update({ code: event.target.value })} />
                </Field>
                <ChoiceField label="Location type" value={form.type} options={LOCATION_TYPES} onChange={(type) => update({ type })} />
                <ChoiceField label="Time zone" value={form.timezone} options={TIMEZONES} onChange={(timezone) => update({ timezone })} />
                <ChoiceField
                  label="Customer"
                  value={form.customerId || 'none'}
                  options={['none', ...customerChoices.map((customer) => customer.id)]}
                  labels={{
                    none: 'Own site',
                    ...Object.fromEntries(customerChoices.map((customer) => [customer.id, customer.label])),
                  }}
                  onChange={(customerId) => update({ customerId: customerId === 'none' ? '' : customerId })}
                />
              </div>
            </Section>
            <Section icon={<Building2 className="h-4 w-4" />} title="Address" description="Enter the address, then pin the exact entrance or attendance point on the map.">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Address line 1">
                  <Input value={form.addressLine1} placeholder="10 Example Street" onChange={(event) => update({ addressLine1: event.target.value })} />
                </Field>
                <Field label="Address line 2">
                  <Input value={form.addressLine2} placeholder="Business Park" onChange={(event) => update({ addressLine2: event.target.value })} />
                </Field>
                <Field label="City">
                  <Input value={form.city} placeholder="Northampton" onChange={(event) => update({ city: event.target.value })} />
                </Field>
                <Field label="Postcode / ZIP">
                  <Input value={form.postcode} placeholder="NN1 1AA" onChange={(event) => update({ postcode: event.target.value })} />
                </Field>
                <Field label="State / County">
                  <Input value={form.state} placeholder="Northamptonshire" onChange={(event) => update({ state: event.target.value })} />
                </Field>
                <ChoiceField label="Country" value={form.country} options={COUNTRIES} onChange={(country) => update({ country })} />
              </div>
            </Section>
            <Section icon={<MapPin className="h-4 w-4" />} title="Map & geofence" description="Pin the location and draw the region where employees are allowed to submit geo-tagged attendance.">
              <div className="mb-3 flex flex-wrap gap-2">
                <MapTool active={form.mapMode === 'pin'} onClick={() => update({ mapMode: 'pin' })}>Pin location</MapTool>
                <MapTool active={form.mapMode === 'region'} onClick={() => update({ mapMode: 'region' })}>Draw region</MapTool>
                <MapTool active={form.mapMode === 'radius'} onClick={() => update({ mapMode: 'radius' })}>Radius</MapTool>
                <MapTool onClick={useMyLocation}>My location</MapTool>
                <MapTool onClick={() => update({ points: [] })}>Clear region</MapTool>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <SitesMap
                  mapsReady={mapsReady}
                  height={280}
                  defaultCenter={position}
                  selectedId="draft"
                  onMapClick={handleMapClick}
                  markers={[{
                    id: 'draft',
                    position,
                    label: form.name || 'Location',
                    polygon: form.mapMode === 'region' && form.points.length >= 3 ? form.points : undefined,
                    radiusM: form.mapMode === 'radius' ? Number(form.radiusM) || 150 : null,
                  }]}
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[160px_160px_1fr]">
                <Field label="Latitude">
                  <Input readOnly value={form.latitude} />
                </Field>
                <Field label="Longitude">
                  <Input readOnly value={form.longitude} />
                </Field>
                <div className="flex items-end text-sm text-slate-500">
                  {form.mapMode === 'region'
                    ? `Pin location or draw at least 3 boundary points. ${form.points.length} point${form.points.length === 1 ? '' : 's'} saved with the location.`
                    : form.mapMode === 'radius'
                      ? 'Circular radius is saved as the allowed attendance region.'
                      : 'Coordinates are saved with the location record.'}
                </div>
              </div>
              {form.mapMode === 'radius' ? (
                <div className="mt-3 max-w-[180px]">
                  <Field label="Radius (metres)">
                    <Input value={form.radiusM} onChange={(event) => update({ radiusM: event.target.value })} />
                  </Field>
                </div>
              ) : null}
              <div className="mt-3 flex gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm text-slate-600">
                <Shield className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <span><b className="text-slate-800">Geo-tagged attendance. </b>When enabled, punch coordinates are saved and compared with this allowed region.</span>
              </div>
            </Section>
            <Section icon={<Smartphone className="h-4 w-4" />} title="Attendance settings" description="Choose which clock methods are available at this location.">
              <div className="grid gap-3 md:grid-cols-2">
                <OptionCard title="QR / Barcode clocking" detail="Allow employees to scan their ID card." checked={form.clockQr} onChange={(clockQr) => update({ clockQr })} />
                <OptionCard title="PIN clocking" detail="Allow employees to clock in using their PIN." checked={form.clockPin} onChange={(clockPin) => update({ clockPin })} />
                <OptionCard title="Kiosk / device clocking" detail="Allow authorised devices at this location." checked={form.clockKiosk} onChange={(clockKiosk) => update({ clockKiosk })} />
                <OptionCard title="GPS / geofence verification" detail="Capture punch coordinates and verify that the employee is inside the allowed map region." checked={form.clockGps} onChange={(clockGps) => update({ clockGps })} />
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <ChoiceField
                  label="Location manager"
                  value={form.managerId || 'none'}
                  options={['none', ...workers.map((worker) => String(worker.id))]}
                  labels={{
                    none: 'None',
                    ...Object.fromEntries(workers.map((worker) => [String(worker.id), worker.display_name || `Worker ${worker.id}`])),
                  }}
                  onChange={(managerId) => update({ managerId: managerId === 'none' ? '' : managerId })}
                />
                <Field label="Default department">
                  <Input value={form.department} placeholder="None" onChange={(event) => update({ department: event.target.value })} />
                </Field>
                <ChoiceField label="Outside-geofence action" value={form.outsideAction} options={GEOFENCE_ACTIONS} onChange={(outsideAction) => update({ outsideAction })} />
                <ChoiceField label="Status" value={form.active ? 'Active' : 'Inactive'} options={['Active', 'Inactive']} onChange={(status) => update({ active: status === 'Active' })} />
              </div>
            </Section>
            {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setScreen('list')}>Cancel</Button>
              <Button type="button" disabled={isSaving} onClick={() => void submit()}>
                {editing ? 'Save Location' : 'Create Location'}
              </Button>
            </div>
          </section>
          <aside className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <MapPin className="h-5 w-5" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">Location & geofence</h3>
            <p className="text-sm text-slate-500">Use a precise map pin for the normal attendance point, then draw a polygon around the permitted area.</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
              <li>Pin the entrance, office or site</li>
              <li>Draw a polygon for irregular sites</li>
              <li>Use a circular radius for simple locations</li>
              <li>Flag or block punches outside the region</li>
            </ul>
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
              <b className="text-slate-800">Privacy. </b>
              Store coordinates only when needed for attendance and make the location policy visible to employees.
            </div>
          </aside>
        </div>
      </div>
    )
  }

  if (screen === 'view' && viewing) {
    const profile = profileOf(viewing)
    const viewingMarker = markerFromLocation(viewing)
    return (
      <div className="space-y-5 pb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{viewing.place}</h1>
            <p className="mt-1 text-sm text-slate-500">{placeLabel(viewing)}</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setScreen('list')}>Back</Button>
            <Button type="button" onClick={() => openEdit(viewing)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SitesMap
            mapsReady={mapsReady}
            height={320}
            defaultCenter={positionOf(viewing) ?? mapCenter}
            markers={viewingMarker ? [viewingMarker] : []}
          />
        </div>
        <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <Detail label="Type" value={viewing.type || 'Site'} />
          <Detail
            label="Customer"
            value={customerLabelFor(viewing) && viewing.client_user ? (
              <Link
                to={customerHrefFor(viewing) || '#'}
                className="text-blue-700 hover:underline"
              >
                {customerLabelFor(viewing)}
              </Link>
            ) : 'Own site'}
          />
          <Detail label="Code" value={profile.code || '—'} />
          <Detail label="Status" value={viewing.active === false ? 'Inactive' : 'Active'} />
          <Detail label="Time zone" value={profile.timezone || '—'} />
          <Detail label="Address" value={viewing.address || '—'} />
          <Detail label="Manager" value={viewing.site_manager_info?.display_name || viewing.site_manager_info?.name || '—'} />
          <Detail label="Department" value={profile.department || 'None'} />
          <Detail label="Outside geofence" value={profile.outside_geofence_action || '—'} />
          <Detail label="Employees" value={String(viewing.assigned_worker_count ?? 0)} />
          <Detail label="Clock devices" value={String(viewing.attendance_devices?.length ?? 0)} />
        </section>
      </div>
    )
  }

  return (
    <div className="space-y-5 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Locations / Sites</h1>
          <p className="mt-1 text-sm text-slate-500">Manage offices, branches, client sites and attendance locations.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
            <Button type="button" size="sm" variant={listView === 'cards' ? 'default' : 'ghost'} className="rounded-lg" onClick={() => setListView('cards')}>
              <LayoutGrid className="mr-2 h-4 w-4" />
              Cards
            </Button>
            <Button type="button" size="sm" variant={listView === 'map' ? 'default' : 'ghost'} className="rounded-lg" onClick={() => {
              setListView('map')
              setSelectedMapId((current) => current ?? mapMarkers[0]?.id ?? null)
            }}>
              <MapIcon className="mr-2 h-4 w-4" />
              Map
            </Button>
          </div>
          <Button type="button" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <SummaryCard icon={<MapPin className="h-5 w-5" />} label="Active locations" value={activeCount} />
        <SummaryCard icon={<Users className="h-5 w-5" />} label="Assigned employees" value={employeeCount} />
        <SummaryCard icon={<Smartphone className="h-5 w-5" />} label="Clock devices" value={deviceCount} />
      </div>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-52 rounded-2xl" />)}
        </div>
      ) : errorMessage ? (
        <div className="rounded-2xl border border-destructive/30 bg-white p-6 text-sm text-destructive">{errorMessage}</div>
      ) : visibleLocations.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <MapPin className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 font-medium text-slate-800">No locations yet</p>
          <p className="mt-1 text-sm text-slate-500">Add an office, branch, or client site to use it for attendance.</p>
        </div>
      ) : listView === 'map' ? (
        <div className="grid items-start gap-3 xl:grid-cols-[300px_minmax(0,1fr)]">
          <div className="max-h-[560px] space-y-2 overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
            {visibleLocations.map((location) => {
              const pinned = Boolean(positionOf(location))
              const selected = selectedMapId === String(location.id)
              return (
                <button
                  key={location.id}
                  type="button"
                  className={`w-full rounded-xl px-3 py-2 text-left ${selected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  onClick={() => {
                    if (pinned) setSelectedMapId(String(location.id))
                  }}
                >
                  <div className="text-sm font-semibold text-slate-900">{location.place}</div>
                  <div className="text-xs text-blue-600">{location.type || 'Site'}</div>
                  {customerLabelFor(location) ? (
                    <div className="text-xs text-slate-600">{customerLabelFor(location)}</div>
                  ) : null}
                  <div className="text-xs text-slate-500">{pinned ? placeLabel(location) : 'Not pinned on the map'}</div>
                </button>
              )
            })}
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {mapMarkers.length === 0 ? (
              <div className="flex h-[560px] flex-col items-center justify-center px-6 text-center">
                <MapPin className="h-8 w-8 text-slate-400" />
                <p className="mt-3 font-medium text-slate-800">No pinned locations</p>
                <p className="mt-1 text-sm text-slate-500">Edit a location and drop a pin to show it on the map.</p>
              </div>
            ) : (
              <SitesMap
                markers={mapMarkers}
                mapsReady={mapsReady}
                height={560}
                defaultCenter={mapCenter}
                selectedId={selectedMapId}
                onSelect={setSelectedMapId}
              />
            )}
            {selectedMapLocation ? (
              <div className="absolute bottom-3 left-3 z-20 w-[min(100%-5.5rem,320px)] rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
                <div className="text-sm font-semibold text-slate-900">{selectedMapLocation.place}</div>
                <div className="text-xs text-blue-600">{selectedMapLocation.type || 'Site'}</div>
                {customerLabelFor(selectedMapLocation) && selectedMapLocation.client_user ? (
                  <Link
                    to={customerHrefFor(selectedMapLocation) || '#'}
                    className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline"
                  >
                    {customerLabelFor(selectedMapLocation)}
                  </Link>
                ) : null}
                <div className="mt-1 text-xs text-slate-500">{placeLabel(selectedMapLocation)}</div>
                <div className="mt-3 flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => openView(selectedMapLocation)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => openEdit(selectedMapLocation)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleLocations.map((location) => (
            <article key={location.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <MapPin className="h-4 w-4" />
                </span>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${location.active === false ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'}`}>
                    {location.active === false ? 'Inactive' : 'Active'}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${location.place}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(location)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openDevices(location)}>Devices</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteTarget(location)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <h3 className="mt-3 text-base font-semibold text-slate-900">{location.place}</h3>
              <div className="text-xs font-medium text-blue-600">{location.type || 'Site'}</div>
              {customerLabelFor(location) ? (
                customerHrefFor(location) ? (
                  <Link
                    to={customerHrefFor(location) || '#'}
                    className="mt-1 inline-block text-sm font-medium text-blue-700 hover:underline"
                  >
                    {customerLabelFor(location)}
                  </Link>
                ) : (
                  <p className="mt-1 text-sm font-medium text-slate-700">{customerLabelFor(location)}</p>
                )
              ) : null}
              <p className="mt-1 text-sm text-slate-500">{placeLabel(location)}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  {location.online_worker_count ?? 0} online
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-slate-400" />
                  {location.offline_worker_count ?? 0} offline
                </span>
                <span className="inline-flex items-center gap-1"><Smartphone className="h-3.5 w-3.5" />{location.attendance_devices?.length ?? 0} devices</span>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={Boolean(deviceLocation)} onOpenChange={(open) => { if (!open) setDeviceLocation(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Devices</DialogTitle>
            <DialogDescription>
              {deviceLocation ? `Attendance devices installed at ${deviceLocation.place}. A device can belong to one location only.` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            {linkedDeviceIds.length ? (
              <div className="divide-y rounded-md border">
                {linkedDeviceIds.map((id) => {
                  const device = deviceById(id)
                  return (
                    <div key={id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="min-w-0 flex-1 truncate">
                        {device?.name || device?.device_sn || `Device ${id}`}{' '}
                        {device?.device_sn ? <span className="font-mono text-xs text-slate-500">({device.device_sn})</span> : null}
                      </span>
                      <Select
                        value={deviceStatuses[id] ?? 'active'}
                        onValueChange={(value) => setDeviceStatuses((current) => ({ ...current, [id]: value as DeviceStatus }))}
                      >
                        <SelectTrigger className="h-8 w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="remove">Delete device</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Unlink device"
                        title="Unlink from this location"
                        onClick={() => setLinkedDeviceIds((current) => current.filter((value) => value !== id))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="rounded-md border border-dashed px-3 py-3 text-sm text-slate-500">No devices linked yet.</p>
            )}
            <Field label="Add device">
              <Select value={deviceChoice || 'none'} onValueChange={addDeviceChoice}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a device" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Choose a device</SelectItem>
                  {deviceOptions.map((device) => (
                    <SelectItem key={device.id} value={String(device.id)}>
                      {(device.name || device.device_sn)} ({device.device_sn}){device.active === false ? ' · Inactive' : ''}
                    </SelectItem>
                  ))}
                  <SelectItem value="new">Add new device</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {deviceChoice === 'new' ? (
              <>
                <Field label="Serial number">
                  <Input value={deviceSerial} onChange={(event) => setDeviceSerial(event.target.value)} placeholder="Device serial number" />
                </Field>
                <Field label="Name">
                  <Input value={deviceName} onChange={(event) => setDeviceName(event.target.value)} placeholder="Optional name" />
                </Field>
              </>
            ) : null}
            {deviceError ? <p className="text-sm text-destructive">{deviceError}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeviceLocation(null)}>Cancel</Button>
            <Button type="button" disabled={deviceSaving || isSaving} onClick={() => { void saveDevices() }}>
              {deviceSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete location</DialogTitle>
            <DialogDescription>
              {deleteTarget ? `Remove ${deleteTarget.place} from locations and sites.` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isSaving}
              onClick={() => {
                if (!deleteTarget) return
                void onDelete(String(deleteTarget.id)).then(() => setDeleteTarget(null))
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-blue-600">{icon}</span>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

function ChoiceField({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  labels?: Record<string, string>
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-1.5 text-sm">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>{labels?.[option] || option}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function MapTool({
  active,
  onClick,
  children,
}: {
  active?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button type="button" size="sm" variant={active ? 'default' : 'outline'} className="rounded-full" onClick={onClick}>
      {children}
    </Button>
  )
}

function OptionCard({
  title,
  detail,
  checked,
  onChange,
}: {
  title: string
  detail: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-3">
      <Checkbox checked={checked} onCheckedChange={(value) => onChange(Boolean(value))} />
      <span>
        <span className="block text-sm font-medium text-slate-800">{title}</span>
        <span className="block text-xs text-slate-500">{detail}</span>
      </span>
    </label>
  )
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900">{value}</div>
    </div>
  )
}
